import React, { useState } from "react";
import { connect } from "react-redux";
import { syncAttendenceByDate, triggerSpecialOvertimeSync } from "../../../actions/AttendenceAction";
import { Button, DatePicker, Table, Alert, Space, Typography, message, Progress, Divider } from "antd";
import moment from "moment";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const Maincontent = ({ syncAttendenceByDate }) => {
  const [loading, setLoading] = useState(false);
  const [syncedData, setSyncedData] = useState(null);
  const [dateRange, setDateRange] = useState([]);

  const [specialSyncLoading, setSpecialSyncLoading] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);

  const handleSync = async () => {
    if (dateRange.length < 2) {
      message.warning("Please select a start and end date");
      return;
    }

    const startDate = dateRange[0];
    const endDate = dateRange[1];
    const totalDays = endDate.diff(startDate, "days") + 1;

    setLoading(true);
    setSyncProgress(0);
    setSyncedData({ updated: [], errors: [] });
    
    const hide = message.loading(`Preparing to sync ${totalDays} days of attendance...`, 0);
    
    try {
      let cumulativeUpdated = [];
      let cumulativeErrors = [];
      
      let current = startDate.clone();
      let dayCount = 0;

      while (current.isSameOrBefore(endDate)) {
        dayCount++;
        const dateStr = current.format("YYYY-MM-DD");
        setSyncProgress(Math.floor(((dayCount - 1) / totalDays) * 100));

        const hideDayMsg = message.loading(`Syncing ${dateStr} (${dayCount}/${totalDays})...`, 0);
        
        try {
          const result = await syncAttendenceByDate(dateStr, dateStr);
          
          if (result && result.updated) {
            cumulativeUpdated = [...cumulativeUpdated, ...result.updated];
          }
          if (result && result.errors) {
            cumulativeErrors = [...cumulativeErrors, ...result.errors];
          }
          
          // Incremental update so user sees results
          setSyncedData({
            updated: [...cumulativeUpdated],
            errors: [...cumulativeErrors]
          });
        } catch (dayError) {
          console.error(`Error syncing ${dateStr}:`, dayError);
          const newError = {
            att_date: dateStr,
            emp_code: "SYSTEM",
            error: "Failed to connect or sync this day"
          };
          cumulativeErrors.push(newError);
          setSyncedData(prev => ({
            ...prev,
            errors: [...prev.errors, newError]
          }));
        } finally {
          hideDayMsg();
        }
        
        current.add(1, "day");
      }

      setSyncProgress(100);
      message.success(`Sync complete! Processed ${cumulativeUpdated.length} records.`);
    } catch (error) {
      message.error("An unexpected error occurred during sync process");
      console.error(error);
    } finally {
      hide();
      setLoading(false);
    }
  };

  const handleSpecialSync = async () => {
    setSpecialSyncLoading(true);
    const hide = message.loading("Calculating special overtime for Feb-Mar 2026...", 0);
    try {
      const result = await triggerSpecialOvertimeSync();
      message.success(result.message || "Special sync complete!");
    } catch (error) {
      message.error("Special sync failed");
      console.error(error);
    } finally {
      hide();
      setSpecialSyncLoading(false);
    }
  };

  const updatedColumns = [
    {
      title: "Employee Code",
      dataIndex: "emp_code",
      key: "emp_code",
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
    },
    {
      title: "Attendance Date",
      dataIndex: "attendanceDate",
      key: "attendanceDate",
    },
    {
      title: "Entry Time",
      dataIndex: "entryTime",
      key: "entryTime",
    },
    {
      title: "Exit Time",
      dataIndex: "exitTime",
      key: "exitTime",
    },
    {
      title: "Late (mins)",
      dataIndex: "lateTime",
      key: "lateTime",
      render: (val) => val ? val.toFixed(2) : "0.00",
    },
    {
      title: "Attended?",
      dataIndex: "isAttended",
      key: "isAttended",
      render: (val) => (val ? "Yes" : "No"),
    },
  ];

  const errorColumns = [
    {
      title: "Date",
      dataIndex: "att_date",
      key: "att_date",
    },
    {
      title: "Employee Code",
      dataIndex: "emp_code",
      key: "emp_code",
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Title level={4}>Sync Attendance from BioTime</Title>
      <Space direction="vertical" size="middle" style={{ marginBottom: "20px", width: "100%" }}>
        <RangePicker 
          onChange={(values) => setDateRange(values)}
          // style={{ width: "100%" }}
          disabled={loading}
        />
        <Text type="secondary">
          This will sync attendance data from all areas (1-5) for the selected date range.
        </Text>
        <Button 
          type="primary" 
          loading={loading} 
          onClick={handleSync}
          size="large"
        >
          {loading ? "Syncing..." : "Sync Attendance"}
        </Button>

        {loading && (
          <div style={{ background: "#f0f5ff", padding: "12px", borderRadius: "8px", border: "1px solid #adc6ff" }}>
            <Progress percent={syncProgress} status="active" />
            <div style={{ marginTop: "4px", textAlign: "center" }}>
              <Text type="secondary">Processing day by day...</Text>
            </div>
          </div>
        )}

        <Divider orientation="left">Special Actions</Divider>
        <div style={{ background: "#fff2f0", padding: "16px", borderRadius: "8px", border: "1px solid #ffccc7" }}>
          <Title level={5}>Ramadan/Special Period Overtime Update</Title>
          <Text>
            Period: <strong>19-02-2026</strong> to <strong>20-03-2026</strong><br/>
            Rule: Overtime starts at <strong>3:30 PM (15:30)</strong> and is counted until <strong>5:35 PM (17:35)</strong>.
          </Text>
          <div style={{ marginTop: "12px" }}>
            <Button 
              danger 
              type="primary" 
              loading={specialSyncLoading} 
              onClick={handleSpecialSync}
            >
              Update Special Overtime
            </Button>
          </div>
        </div>
      </Space>

      {syncedData && (
        <>
          <Alert
            message={`${syncedData.updated.length} records synced successfully across all areas.`}
            type="success"
            showIcon
            style={{ marginBottom: "16px" }}
          />
          <Table
            columns={updatedColumns}
            dataSource={syncedData.updated.map((item, index) => ({ ...item, key: index }))}
            pagination={{ 
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} records`
            }}
            title={() => "Synced Attendance Records"}
            bordered
            size="small"
            scroll={{ x: 1000 }}
          />

          {syncedData.errors.length > 0 && (
            <>
              <Alert
                message={`${syncedData.errors.length} errors encountered during sync.`}
                type="error"
                showIcon
                style={{ margin: "24px 0 16px" }}
              />
              <Table
                columns={errorColumns}
                dataSource={syncedData.errors.map((err, i) => ({ ...err, key: i }))}
                pagination={{ 
                  pageSize: 20,
                  showTotal: (total) => `Total ${total} errors`
                }}
                title={() => "Sync Errors"}
                bordered
                size="small"
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default connect(null, { syncAttendenceByDate, triggerSpecialOvertimeSync })(Maincontent);